import { useTranslation } from "react-i18next";
import Subheading from "../../../atoms/typography/Subheading";
import TextField from "../../../atoms/inputs/TextField";
import { useState, useEffect, type FC } from "react";
import { Row } from "react-grid-system";
import { theme } from "../../../../theme";
import BodySmall from "../../../atoms/typography/BodySmall";
import IconButton from "../../../atoms/buttons/IconButton";
import { SaveIconMedium } from "../../../atoms/icons/SaveIcon";
import { useUpdateInvoiceIssuer } from "../../../../graphql/hooks/invoices/useUpdateInvoiceIssuer";
import Alert from "../../../molecules/Alert";
import Tooltip from "../../../molecules/Tooltip";
import ChevronButton from "../../../atoms/buttons/ChevronButtons";
import { useRegisterSaveSection } from "../SaveSectionsContext";
import { SlideOutHeight } from "../../../atoms/SlideOutHeight";

interface Props {
  issuerName?: string;
  issuerClientIdentifier?: string;
  issuerReference?: string;
  invoiceId: string;
  isEditable: boolean;
}

const InvoiceIssuerDetailsSection: FC<Props> = ({
  issuerName,
  issuerClientIdentifier,
  issuerReference,
  invoiceId,
  isEditable,
}) => {
  const { t } = useTranslation();
  const [saveSuccessful, setSaveSuccessful] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [reference, setReference] = useState(issuerReference || "");
  const [dataHasBeenUpdated, setDataHasBeenUpdated] = useState(false);
  const [open, setOpen] = useState(true);

  useEffect(() => {
    setReference(issuerReference || "");
  }, [issuerReference]);

  const onCompletedUpdateIssuerData = (res?: any) => {
    if (res?.invoice?.updateInvoiceIssuer?.success) {
      setSaveSuccessful(true);
      setErrorMessage("");
      setTimeout(() => {
        setSaveSuccessful(false);
      }, 5000);
      setDataHasBeenUpdated(false);
    } else {
      setErrorMessage(t("invoices.updateIssuerDataFailed"));
    }
  };
  const onErrorUpdateIssuerData = (e?: any) => {
    setErrorMessage(e?.toString() ?? t("invoices.updateIssuerDataFailed"));
  };
  const { updateInvoiceIssuer, error, loading } = useUpdateInvoiceIssuer({
    onCompleted: onCompletedUpdateIssuerData,
    onError: onErrorUpdateIssuerData,
  });

  const onClickSaveSection = async () => {
    try {
      await updateInvoiceIssuer({
        variables: {
          input: {
            clientIdentifier: issuerClientIdentifier,
            invoiceId: invoiceId,
            reference: reference,
          },
        },
      });
    } catch (e) {
      setErrorMessage(e?.toString() ?? t("invoices.updateIssuerDataFailed"));
    }
  };

  useRegisterSaveSection({
    id: "issuer",
    label: t("invoiceValidation.saveIssuerDetails"),
    isDirty: dataHasBeenUpdated,
    onSave: onClickSaveSection,
    loading,
  });

  return (
    <div
      style={{
        backgroundColor: dataHasBeenUpdated
          ? theme.colors.info05
          : "transparent",
        padding: 8,
        borderRadius: 4,
      }}
    >
      <Row nogutter justify="between">
        <Subheading>{t("invoices.issuer")}</Subheading>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <ChevronButton
            type={open ? "up" : "down"}
            onClick={() => setOpen(!open)}
          />
          {saveSuccessful ? (
            <BodySmall fontweight={500} color={theme.colors.success}>
              {t("random.saved")}
            </BodySmall>
          ) : (
            <Tooltip text={t("invoiceValidation.saveSection")}>
              <IconButton
                icon={
                  <SaveIconMedium
                    color={
                      dataHasBeenUpdated
                        ? theme.colors.white
                        : theme.colors.text
                    }
                  />
                }
                onClick={onClickSaveSection}
                size="medium"
                loading={loading}
                color={
                  dataHasBeenUpdated ? theme.colors.info : theme.colors.info05
                }
                disabled={!isEditable}
              />
            </Tooltip>
          )}
        </div>
      </Row>
      <SlideOutHeight isVisible={open}>
        <div style={{ display: "flex", gap: 24, padding: "8px 0" }}>
          <TextField
            label={t("invoices.name")}
            value={issuerName}
            name="issuerName"
            disabled
            fullWidth
          />
          <TextField
            label={"Id (Org/Vat)"}
            value={issuerClientIdentifier}
            disabled
            fullWidth
            name="issuerClientIdentifier"
          />
        </div>
        <div style={{ display: "flex", gap: 24, padding: "8px 0" }}>
          <TextField
            label={t("invoices.reference")}
            value={reference}
            name="issuerReference"
            onChange={(value) => {
              if (!dataHasBeenUpdated) setDataHasBeenUpdated(true);
              setReference(value);
            }}
            fullWidth
            disabled={!isEditable}
          />
        </div>
      </SlideOutHeight>
      {(error || errorMessage) && (
        <Alert
          type="error"
          title={t("invoices.updateIssuerDataFailed")}
          message={errorMessage || error?.toString()}
        />
      )}
    </div>
  );
};

export default InvoiceIssuerDetailsSection;
